/**
 * useInterimUpdates Hook
 * Manages mid-call ticket updates: manual trigger, auto-update timer, delta tracking.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useCallRecordingContext } from '@/contexts/CallRecordingContext'
import { performInterimUpdate } from '@/hooks/useCallRecordings'
import { blobToBase64, getAudioFormat } from '@/hooks/useCallRecording'

export type AutoUpdateInterval = 0 | 5 | 15 | 30

interface InterimUpdateHistoryEntry {
  number: number
  timestamp: Date
  wordCount: number
  success: boolean
}

interface InterimUpdateState {
  isUpdating: boolean
  lastUpdateTime: Date | null
  lastUpdateNumber: number
  autoUpdateInterval: AutoUpdateInterval
  error: string | null
  updateHistory: InterimUpdateHistoryEntry[]
}

export interface UseInterimUpdatesReturn extends InterimUpdateState {
  triggerUpdate: () => Promise<void>
  setAutoUpdateInterval: (interval: AutoUpdateInterval) => void
  resetInterimState: () => void
}

export function useInterimUpdates(
  sysId: string | null,
  table: string | null
): UseInterimUpdatesReturn {
  const { isRecording, isPaused, duration, extractPartialAudio } = useCallRecordingContext()

  const [state, setState] = useState<InterimUpdateState>({
    isUpdating: false,
    lastUpdateTime: null,
    lastUpdateNumber: 0,
    autoUpdateInterval: 0,
    error: null,
    updateHistory: [],
  })

  const previousTranscriptRef = useRef<string | null>(null)
  const updateNumberRef = useRef(0)
  const autoTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const remainingMsRef = useRef<number | null>(null)
  const nextUpdateTimeRef = useRef<number | null>(null)
  const isUpdatingRef = useRef(false)

  // Store latest values in refs so the timer callback always reads current state
  // without needing to be a dependency of the timer useEffect.
  const durationRef = useRef(duration)
  durationRef.current = duration

  const sysIdRef = useRef(sysId)
  sysIdRef.current = sysId

  const tableRef = useRef(table)
  tableRef.current = table

  const isRecordingRef = useRef(isRecording)
  isRecordingRef.current = isRecording

  const extractPartialAudioRef = useRef(extractPartialAudio)
  extractPartialAudioRef.current = extractPartialAudio

  const triggerUpdate = useCallback(async () => {
    const currentSysId = sysIdRef.current
    if (!currentSysId || !isRecordingRef.current || isUpdatingRef.current) return

    const blob = await extractPartialAudioRef.current()
    if (!blob) return

    isUpdatingRef.current = true
    setState(prev => ({ ...prev, isUpdating: true, error: null }))

    try {
      const base64 = await blobToBase64(blob)
      const format = getAudioFormat(blob.type)
      updateNumberRef.current += 1
      const updateNum = updateNumberRef.current

      const result = await performInterimUpdate({
        audioData: base64,
        audioFormat: format,
        durationSeconds: durationRef.current,
        sysId: currentSysId,
        table: tableRef.current || 'incident',
        previousTranscript: previousTranscriptRef.current || undefined,
        updateNumber: updateNum,
      })

      // Store transcript for next delta (even if note wasn't added to SN)
      if (result.transcript) {
        previousTranscriptRef.current = result.transcript
      }

      setState(prev => ({
        ...prev,
        isUpdating: false,
        lastUpdateTime: new Date(),
        lastUpdateNumber: updateNum,
        error: null,
        updateHistory: [
          ...prev.updateHistory,
          {
            number: updateNum,
            timestamp: new Date(),
            wordCount: result.wordCount,
            success: result.success,
          },
        ],
      }))
    } catch (err) {
      setState(prev => ({
        ...prev,
        isUpdating: false,
        error: err instanceof Error ? err.message : 'Update failed',
      }))
    } finally {
      isUpdatingRef.current = false
    }
  }, []) // No deps — reads everything from refs

  // Store triggerUpdate in a ref so the timer always calls the latest version
  const triggerUpdateRef = useRef(triggerUpdate)
  triggerUpdateRef.current = triggerUpdate

  const setAutoUpdateInterval = useCallback((interval: AutoUpdateInterval) => {
    setState(prev => ({ ...prev, autoUpdateInterval: interval }))
  }, [])

  const resetInterimState = useCallback(() => {
    if (autoTimerRef.current) {
      clearInterval(autoTimerRef.current)
      autoTimerRef.current = null
    }
    previousTranscriptRef.current = null
    updateNumberRef.current = 0
    remainingMsRef.current = null
    nextUpdateTimeRef.current = null
    isUpdatingRef.current = false
    setState({
      isUpdating: false,
      lastUpdateTime: null,
      lastUpdateNumber: 0,
      autoUpdateInterval: 0,
      error: null,
      updateHistory: [],
    })
  }, [])

  // Auto-update timer management
  // Deps are only stable values: autoUpdateInterval, isRecording, isPaused.
  // The callback is read from triggerUpdateRef so duration changes don't reset the timer.
  useEffect(() => {
    const intervalMs = state.autoUpdateInterval * 60 * 1000

    // Clear existing timer
    if (autoTimerRef.current) {
      clearInterval(autoTimerRef.current)
      autoTimerRef.current = null
    }

    // Don't start timer if disabled, not recording, or paused
    if (state.autoUpdateInterval === 0 || !isRecording || isPaused) {
      // If paused, save remaining time
      if (isPaused && nextUpdateTimeRef.current && state.autoUpdateInterval > 0) {
        remainingMsRef.current = Math.max(0, nextUpdateTimeRef.current - Date.now())
      }
      return
    }

    // Determine initial delay (remaining time from pause, or full interval)
    const initialDelay = remainingMsRef.current !== null && remainingMsRef.current > 0
      ? remainingMsRef.current
      : intervalMs
    remainingMsRef.current = null

    // Set timeout for first tick, then start regular interval
    const timeoutId = setTimeout(() => {
      triggerUpdateRef.current()
      nextUpdateTimeRef.current = Date.now() + intervalMs
      autoTimerRef.current = setInterval(() => {
        triggerUpdateRef.current()
        nextUpdateTimeRef.current = Date.now() + intervalMs
      }, intervalMs)
    }, initialDelay)

    nextUpdateTimeRef.current = Date.now() + initialDelay

    return () => {
      clearTimeout(timeoutId)
      if (autoTimerRef.current) {
        clearInterval(autoTimerRef.current)
        autoTimerRef.current = null
      }
    }
  }, [state.autoUpdateInterval, isRecording, isPaused])

  // Reset when recording stops
  useEffect(() => {
    if (!isRecording) {
      if (autoTimerRef.current) {
        clearInterval(autoTimerRef.current)
        autoTimerRef.current = null
      }
      nextUpdateTimeRef.current = null
      remainingMsRef.current = null
    }
  }, [isRecording])

  return {
    ...state,
    triggerUpdate,
    setAutoUpdateInterval,
    resetInterimState,
  }
}
